import { Alert, Button, Card, Space, Table, Tag, Typography } from "antd";
import type { TableColumnsType } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";
import { useAuth } from "../app/auth.tsx";
import { PAGE_SIZE, todayIso, useServiceKinds, useServicePrices, type ServiceKind, type ServicePrice } from "../app/billing.ts";
import { RuleHistoryModal, ScopeSwitch, SumText, ValidityText, VersionActions, VersionStatus, useDeleteVersion } from "./BillingRuleParts.tsx";
import { EndVersionModal } from "./EndVersionModal.tsx";
import { ServiceKindModal, ServicePriceModal } from "./ServicePriceModals.tsx";

async function loadHistory(id: string): Promise<ServicePrice[]> {
  const { data, error, response } = await api.GET("/billing/service-prices/{id}/history", { params: { path: { id } } });
  if (!data) throw new ApiError(response.status, error);
  return data;
}

/** Prices of the services the school charges for apart from tuition (spec story 58); the kinds are entered by staff. */
export function ServicePricesTab() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("billing.manageRules");
  const [all, setAll] = useState(false);
  const [page, setPage] = useState(1);
  const prices = useServicePrices(all ? undefined : todayIso(), page);
  const kinds = useServiceKinds();
  const [editing, setEditing] = useState<ServicePrice | "new" | undefined>(undefined);
  const [editingKind, setEditingKind] = useState<ServiceKind | "new" | undefined>(undefined);
  const [historyOf, setHistoryOf] = useState<ServicePrice | undefined>(undefined);
  const [endOf, setEndOf] = useState<ServicePrice | undefined>(undefined);
  const remove = useDeleteVersion((id) => api.DELETE("/billing/service-prices/{id}", { params: { path: { id } } }));

  const versionColumns: TableColumnsType<ServicePrice> = [
    { key: "amount", title: t("billing.services.amount"), align: "right", render: (_, row) => <SumText value={row.amount} /> },
    { key: "validity", title: t("billing.validity.title"), render: (_, row) => <ValidityText version={row} /> },
    { key: "status", title: t("billing.status.title"), render: (_, row) => <VersionStatus version={row} /> },
  ];
  const columns: TableColumnsType<ServicePrice> = [
    { key: "kind", title: t("billing.services.kind"), dataIndex: "serviceKindName" },
    ...versionColumns,
    {
      key: "actions",
      title: t("common.actions"),
      width: 170,
      render: (_, row) => (
        <VersionActions
          version={row}
          label={row.serviceKindName}
          canManage={canManage}
          deleting={remove.isPending && remove.variables === row.id}
          onHistory={() => setHistoryOf(row)}
          onEdit={() => setEditing(row)}
          onDelete={() => remove.mutate(row.id)}
          onEnd={() => setEndOf(row)}
        />
      ),
    },
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <Typography.Text type="secondary">{t("billing.services.hint")}</Typography.Text>
      <Card
        size="small"
        title={t("billing.services.kindsTitle")}
        extra={canManage && <Button size="small" icon={<PlusOutlined />} onClick={() => setEditingKind("new")}>{t("billing.services.createKind")}</Button>}
      >
        {kinds.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void kinds.refetch()}>{t("app.retry")}</Button>} />}
        {kinds.data?.length === 0 && <Typography.Text type="secondary">{t("billing.services.noKinds")}</Typography.Text>}
        <Space size={[8, 8]} wrap>
          {(kinds.data ?? []).map((kind) => (
            <Tag key={kind.id} style={{ paddingInline: 8, paddingBlock: 2 }}>
              {kind.name}
              {canManage && (
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  aria-label={t("billing.services.renameKindNamed", { name: kind.name })}
                  onClick={() => setEditingKind(kind)}
                />
              )}
            </Tag>
          ))}
        </Space>
      </Card>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between" }}>
        <ScopeSwitch all={all} onChange={(next) => { setAll(next); setPage(1); }} />
        {canManage && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing("new")}>
            {t("billing.services.create")}
          </Button>
        )}
      </div>
      {prices.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void prices.refetch()}>{t("app.retry")}</Button>} />}
      <Table<ServicePrice>
        rowKey="id"
        size="middle"
        loading={prices.isPending}
        columns={columns}
        dataSource={prices.data?.items ?? []}
        scroll={{ x: true }}
        locale={{ emptyText: t("billing.services.empty") }}
        pagination={{ current: page, pageSize: PAGE_SIZE, total: Number(prices.data?.totalCount ?? 0), showSizeChanger: false, onChange: setPage, hideOnSinglePage: true }}
      />
      {canManage && <ServicePriceModal target={editing} onClose={() => setEditing(undefined)} />}
      {canManage && <ServiceKindModal target={editingKind} onClose={() => setEditingKind(undefined)} />}
      {canManage && (
        <EndVersionModal
          version={endOf}
          label={endOf ? endOf.serviceKindName : ""}
          end={(id, validTo) => api.POST("/billing/service-prices/{id}/end", { params: { path: { id } }, body: { validTo } })}
          onClose={() => setEndOf(undefined)}
        />
      )}
      <RuleHistoryModal<ServicePrice>
        kind="service-prices"
        entityType="ServicePrice"
        version={historyOf}
        title={historyOf ? t("billing.historyTitle", { name: historyOf.serviceKindName }) : ""}
        columns={versionColumns}
        load={loadHistory}
        onClose={() => setHistoryOf(undefined)}
      />
    </Space>
  );
}
